import { useCallback, useRef } from 'react';
import { CombinationConfig, KeyCombination } from './types';

const useKeyCombinationEvent = (config: CombinationConfig, callback?: (event: KeyboardEvent) => void) => {
  const keyPressHistory = useRef<KeyCombination[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const COMBINATION_TIMEOUT = config.timeout || 1000; // Default 1 second timeout

  const handleKeyCombination = useCallback((event: KeyboardEvent) => {
    const { key, ctrlKey, metaKey, shiftKey, altKey } = event;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add current key press to history
    const currentKeyPress: KeyCombination = {
      key,
      ctrlKey,
      metaKey,
      shiftKey,
      altKey
    };

    keyPressHistory.current.push(currentKeyPress);

    // Check for simple combination
    if (config.simple) {
      const { simple } = config;
      
      // Handle cross-platform modifier keys (Ctrl for Windows, Cmd for Mac)
      const isCtrlOrMetaPressed = ctrlKey || metaKey;
      
      const isSimpleMatch = key === simple.key
                          && (simple.ctrlKey || simple.metaKey ? isCtrlOrMetaPressed : !isCtrlOrMetaPressed)
                          && (simple.shiftKey ? shiftKey : !shiftKey)
                          && (simple.altKey ? altKey : !altKey);

      if (isSimpleMatch) {
        // Clear history and execute callback if provided
        keyPressHistory.current = [];
        if (callback) {
          callback(event);
        }
        return true;
      }
    }

    // Check for double combination
    if (config.double && keyPressHistory.current.length >= 2) {
      const firstPress = keyPressHistory.current[0];
      const secondPress = keyPressHistory.current[1];
      const { first, second } = config.double;

      // Handle cross-platform modifier keys for first press
      const isFirstCtrlOrMetaPressed = firstPress.ctrlKey || firstPress.metaKey;
      
      const isFirstMatch = firstPress.key === first.key
                          && (first.ctrlKey || first.metaKey ? isFirstCtrlOrMetaPressed : !isFirstCtrlOrMetaPressed)
                          && (first.shiftKey ? firstPress.shiftKey : !firstPress.shiftKey)
                          && (first.altKey ? firstPress.altKey : !firstPress.altKey);

      // Handle cross-platform modifier keys for second press
      const isSecondCtrlOrMetaPressed = secondPress.ctrlKey || secondPress.metaKey;
      
      const isSecondMatch = secondPress.key === second.key
                           && (second.ctrlKey || second.metaKey ? isSecondCtrlOrMetaPressed : !isSecondCtrlOrMetaPressed)
                           && (second.shiftKey ? secondPress.shiftKey : !secondPress.shiftKey)
                           && (second.altKey ? secondPress.altKey : !secondPress.altKey);

      if (isFirstMatch && isSecondMatch) {
        // Clear history and execute callback if provided
        keyPressHistory.current = [];
        if (callback) {
          callback(event);
        }
        return true;
      }
    }

    // Set timeout to clear history if no combination is detected
    timeoutRef.current = setTimeout(() => {
      keyPressHistory.current = [];
    }, COMBINATION_TIMEOUT);

    return false;
  }, [config, COMBINATION_TIMEOUT, callback]);

  return handleKeyCombination;
};

export default useKeyCombinationEvent;
